import {
  IraDistribution,
  IraContributionDetails,
  IraDistributionDetails,
  IraContribution,
  AddressDetails,
  TaxWithholdingTypes
} from '../'

export enum AccountType {
  Margin = 'MARGIN',
  Cash = 'CASH',
  Dvp = 'DVP',
  Short = 'SHORT',
  Unknown = 'UNKNOWN'
}

export enum PhoneNumberType {
  Home = 'HOME',
  Business = 'BUSINESS',
  Cellular = 'CELLULAR',
  Fax = 'FAX',
  Telex = 'TELEX',
  OnCheque = 'ON_CHEQUE',
  Other = 'OTHER'
}

export interface PhoneNumber {
  phoneNumber: string
  phoneNumberType: PhoneNumberType
  extension: string
}

export type TransferTypes =
  | 'POSITION_JOURNAL'
  | 'ACH'
  | 'WIRE'
  | 'CHECK'
  | 'CASH_JOURNAL'
  | 'FEE'
  | 'CREDIT'
  | 'PAYPAL'
  | 'RTP'

export enum BankAccountType {
  Checking = 'CHECKING',
  Savings = 'SAVINGS'
}

export type DistributionReason =
  | 'NORMAL'
  | 'DISABILITY'
  | 'SOSEPP'
  | 'PREMATURE'
  | 'DEATH'
  | 'EXCESS_CONTRIBUTION_REMOVAL_BEFORE_TAX_DEADLINE'
  | 'EXCESS_CONTRIBUTION_REMOVAL_AFTER_TAX_DEADLINE'
  | 'ROLLOVER_TO_QUALIFIED_PLAN'
  | 'ROLLOVER_TO_IRA'
  | 'PLAN_LOAN_401K'
  | 'TRANSFER'
  | 'RECHARACTERIZATION_PRIOR_YEAR'
  | 'RECHARACTERIZATION_CURRENT_YEAR'
  | 'CONVERSION'
  | 'MANAGEMENT_FEE'
  | 'PREMATURE_SIMPLE_IRA_LESS_THAN_2_YEARS'
  | 'NORMAL_ROTH_IRA_GREATER_THAN_5_YEARS'
  | 'QUALIFIED_CHARITABLE_DISTRIBUTION'

export enum DistributionReasonEnum {
  Normal = 'NORMAL',
  Disability = 'DISABILITY',
  SOSEPP = 'SOSEPP',
  Premature = 'PREMATURE',
  Death = 'DEATH',
  ExcessContributionRemovalBeforeTaxDeadline = 'EXCESS_CONTRIBUTION_REMOVAL_BEFORE_TAX_DEADLINE',
  ExcessContributionRemovalAfterTaxDeadline = 'EXCESS_CONTRIBUTION_REMOVAL_AFTER_TAX_DEADLINE',
  RolloverToQualifiedPlan = 'ROLLOVER_TO_QUALIFIED_PLAN',
  RolloverToIra = 'ROLLOVER_TO_IRA',
  PlanLoan401k = 'PLAN_LOAN_401K',
  Transfer = 'TRANSFER',
  RecharacterizationPriorYear = 'RECHARACTERIZATION_PRIOR_YEAR',
  RecharacterizationCurrentYear = 'RECHARACTERIZATION_CURRENT_YEAR',
  Conversion = 'CONVERSION',
  ManagementFee = 'MANAGEMENT_FEE',
  PrematureSimpleIraLessThanTwoYears = 'PREMATURE_SIMPLE_IRA_LESS_THAN_2_YEARS',
  NormalRothIraGreaterThanFiveYears = 'NORMAL_ROTH_IRA_GREATER_THAN_5_YEARS',
  QualifiedCharitableDistribution = 'QUALIFIED_CHARITABLE_DISTRIBUTION'
}

export type BeneficiaryDetails = {
  account?: string
  isThirdParty?: boolean
  name?: string
  addressDetails?: AddressDetails
}

export type CheckBeneficiaryDetails = {
  isThirdParty?: boolean
  name?: string
  addressDetails?: AddressDetails
}

export type FrequencyUnit = 'DAY' | 'WEEK' | 'MONTH'

export type IntermediaryDetails = {
  account: string
  name: string
  addressDetails: AddressDetails
}

export type RecipientBankDetails = {
  identifierDetails: RecipientBankIdentifierDetails
  name?: string
  city?: string
  stateProvince?: string
  country?: string
  postalCode?: string
  additionalInformation?: string
}

export type RecipientBankIdentifierDetails = {
  id: string
  type: 'ABA' | 'BIC'
}

export interface ScheduledTransferResponse {
  id: string
  externalId?: string
  transferType: TransferType
  startDate: string
  amount: number
  direction: TransferDirection
  frequencyUnit?: FrequencyUnit
  frequencyInterval?: number
  occurrences?: number
  status: TransferStatus
  comment?: string
  sourceAccount?: string
  destinationAccount?: string
  iraDistributionDetails?: IraDistributionDetails
  iraContributionDetails?: IraContributionDetails
  cashJournalDetails?: {
    account?: string
    contraAccountId?: string
  }
  isFullDisbursement: boolean
}

export interface ScheduledTransferAchResponse extends ScheduledTransferResponse {
  achRelationshipId?: string
  iraDistribution?: IraDistribution
  iraContribution?: IraContribution
}

export interface ScheduledTransferCashJournalResponse extends ScheduledTransferResponse {
  sourceAccount?: string
  destinationAccount?: string
  iraDistribution?: IraDistribution
  iraContribution?: IraContribution
}

export interface ScheduledTransferCheckResponse extends ScheduledTransferResponse {
  account?: string
  fullDisbursement?: boolean
  deliveryMethod?: 'STANDARD' | 'OVERNIGHT'
  beneficiaryDetails?: CheckBeneficiaryDetails
  iraDistributionDetails?: IraDistribution
}

export interface ScheduledTransferWireResponse extends ScheduledTransferResponse {
  account?: string
  iraDistributionDetails?: IraDistribution
  recipientBankDetails: RecipientBankDetails
  intermediaryDetails?: IntermediaryDetails
  beneficiaryDetails?: BeneficiaryDetails
  fullDisbursement?: boolean
}

export type ScheduledTransferUpdateRequest = {
  amount: number
}

export type TransferDirection = 'DEPOSIT' | 'WITHDRAWAL' | 'JOURNAL'

export type TransferStatus = 'ACTIVE' | 'CANCELED' | 'COMPLETE'

export type TransferType = 'ACH' | 'WIRE' | 'CASH_JOURNAL' | 'CHECK'

export type TaxWithholding = {
  valueType: TaxWithholdingTypes
  value: number
}
export enum TaxWithholdingValueType {
  Fixed = 'FIXED',
  Percent = 'PERCENTAGE'
}
export enum TransferMechanismWithJournal {
  PositionJournal = 'POSITION_JOURNAL'
}

export enum TransferMechanism {
  Ach = 'ACH',
  Wire = 'WIRE',
  Check = 'CHECK',
  Journal = 'CASH_JOURNAL',
  Fee = 'FEE',
  Credit = 'CREDIT',
  PositionJournal = 'POSITION_JOURNAL',
  Rtp = 'RTP'
}

export enum CashDirectionWithJournal {
  Journal = 'JOURNAL'
}

export enum CashDirection {
  Journal = 'JOURNAL',
  Deposit = 'DEPOSIT',
  Withdrawal = 'WITHDRAWAL'
}

export enum HTTPMethod {
  GET = 'GET',
  PUT = 'PUT',
  POST = 'POST',
  HEAD = 'HEAD',
  DELETE = 'DELETE'
}

export interface ApexApiError extends Error {
  response?: Response
  status?: number
  message: string
  body?: any
}
